import React, { useState, useEffect, useRef } from 'react';
import { Send, X } from 'lucide-react';
import './Kit19ChatWidget.css';

const CHAT_API_BASE_URL = 'https://chatwidget.Kit19.com/chat';

const Kit19ChatWidget = ({ embedded = false }) => {
  const [isOpen, setIsOpen] = useState(embedded ? true : false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('kit19_chat_session_id') || '');
  const [visitorId, setVisitorId] = useState(() => localStorage.getItem('kit19_chat_visitor_id') || '');
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  const chatBoxBodyRef = useRef(null);

  // Group messages by date string
  const groupedMessages = messages.reduce((acc, msg) => {
    const dateStr = msg.timestamp ? new Date(msg.timestamp).toLocaleDateString() : new Date().toLocaleDateString();
    if (!acc[dateStr]) acc[dateStr] = [];
    acc[dateStr].push(msg);
    return acc;
  }, {});

  const formatTimestamp = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
  };

  const getDateLabel = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
        return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
    } else {
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    }
  };

  const scrollToBottom = () => {
    if (chatBoxBodyRef.current) {
      chatBoxBodyRef.current.scrollTop = chatBoxBodyRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (embedded && !isInitialized) {
      initChat();
    }
  }, [embedded, isInitialized]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, suggestions]);

  const initChat = async () => {
    if (isInitialized) return;
    setIsTyping(true);
    try {
      const response = await fetch(`${CHAT_API_BASE_URL}/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_id: visitorId,
          session_id: sessionId
        }),
      });
      if (!response.ok) throw new Error('Failed to initialize chat session.');
      
      const data = await response.json();
      const newSessionId = data.session_id;
      const newVisitorId = data.visitor_id;
      
      setSessionId(newSessionId);
      setVisitorId(newVisitorId);
      localStorage.setItem('kit19_chat_session_id', newSessionId);
      localStorage.setItem('kit19_chat_visitor_id', newVisitorId);
      
      let initMessages = [];
      if (data.history && data.history.length > 0) {
        initMessages = data.history.map(msg => ({ sender: msg.sender, text: msg.text, timestamp: msg.timestamp }));
      } else {
        initMessages = [{ sender: 'Bot', text: data.welcome_message, timestamp: data.timestamp }];
      }
      
      setMessages(initMessages);
      setSuggestions(data.suggestions || []);
      setIsInitialized(true);
    } catch (error) {
      console.error('Chat API Init Error:', error);
      setMessages([{ sender: 'Bot', text: 'Oops! Could not connect to the chat service.', timestamp: new Date().toISOString() }]);
    } finally {
      setIsTyping(false);
    }
  };

  const fetchChatResponse = async (messageText) => {
    setIsTyping(true);
    try {
      const payload = { session_id: sessionId, message: messageText, visitor_id: visitorId };
      const response = await fetch(`${CHAT_API_BASE_URL}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'API Error');
      }
      const data = await response.json();
      
      setMessages(prev => [...prev, { sender: 'Bot', text: data.response, timestamp: data.timestamp }]);
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Chat API Error:', error);
      setMessages(prev => [...prev, { sender: 'Bot', text: 'Oops! Something went wrong. Please try refreshing.', timestamp: new Date().toISOString() }]);
    } finally {
      setIsTyping(false);
    }
  };

  const sendMessage = (text) => {
    const trimmedMessage = text.trim();
    if (trimmedMessage === '') return;
    
    setSuggestions([]);
    setMessages(prev => [...prev, { sender: 'User', text: trimmedMessage, timestamp: new Date().toISOString() }]);
    setInputValue('');
    fetchChatResponse(trimmedMessage);
  };

  const handleLauncherClick = () => {
    setIsOpen(true);
    if (!isInitialized) {
      initChat();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion === "Something else...") {
      setSuggestions([]);
      setMessages(prev => [...prev, { sender: 'Bot', text: 'Sure, please type your question below.', timestamp: new Date().toISOString() }]);
    } else {
      sendMessage(suggestion);
    }
  };

  const allSuggestions = suggestions.length > 0 ? [...suggestions, "Something else..."] : [];

  return (
    <>
      {/* Launcher Button */}
      {!isOpen && !embedded && (
        <div id="kit19-chat-launcher" className="kit19-launcher" onClick={handleLauncherClick}>
          <div className="kit19-launcher-logo">
            <img src="/assets/custom/NewIndexPage/logobig.png" alt="Kit19" onError={(e) => { e.target.src = '/icon.png'; }} />
          </div>
          <span>Chat with Us</span>
        </div>
      )}

      {/* Chat Widget */}
      <div id={embedded ? undefined : "kit19-chat-widget"} className={`kit19-root ${(!isOpen && !embedded) ? 'kit19-hidden' : ''} ${embedded ? 'kit19-embedded' : ''}`}>
        <div className="kit19-header">
          <div className="kit19-hdr-left">
            <div className="kit19-hdr-logo">
              <img src="/assets/custom/NewIndexPage/logobig.png" alt="Kit19" onError={(e) => { e.target.src = '/icon.png'; }} />
            </div>
            <div className="kit19-hdr-info">
              <span className="kit19-hdr-title">Kit19 AI Assistant</span>
              <span className="kit19-hdr-status">Online</span>
            </div>
          </div>
          {!embedded && (
            <div className="kit19-hdr-right">
              <button type="button" className="kit19-close-btn" title="Close" onClick={() => setIsOpen(false)}>
                <X size={20} />
              </button>
            </div>
          )}
        </div>
        
        <div className="kit19-body" ref={chatBoxBodyRef}>
          <div className="kit19-chat-log">
            {Object.keys(groupedMessages).map(dateStr => (
              <React.Fragment key={dateStr}>
                <div className="kit19-date-separator">{getDateLabel(dateStr)}</div>
                {groupedMessages[dateStr].map((msg, idx) => (
                  <div key={idx} className={`kit19-message-row ${msg.sender === 'User' ? 'kit19-align-right' : 'kit19-align-left'}`}>
                    {msg.sender === 'Bot' && (
                      <div className="kit19-bot-avatar">
                        <span style={{ fontSize: '1.2em' }}>🤖</span>
                      </div>
                    )}
                    <div className={`kit19-message-bubble kit19-fade-in ${msg.sender === 'User' ? 'kit19-user' : 'kit19-bot'}`}>
                      <div className="kit19-message-content" dangerouslySetInnerHTML={{ __html: msg.text.replace(/\n/g, '<br>') }} />
                      <div className="kit19-message-timestamp">{formatTimestamp(msg.timestamp)}</div>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
            
            {allSuggestions.length > 0 && (
              <div className="kit19-suggestions-bar kit19-fade-in">
                {allSuggestions.map((suggestion, idx) => (
                  <button key={idx} className="kit19-suggestion-btn" onClick={() => handleSuggestionClick(suggestion)}>
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {isTyping && (
          <div className="kit19-typing-indicator">
            <div className="kit19-typing-dots">
              <span></span><span></span><span></span>
            </div>
            <span>Assistant is typing...</span>
          </div>
        )}
        
        <div className="kit19-footer">
          <input 
            type="text" 
            className="kit19-input" 
            placeholder="Ask anything about Kit19..." 
            maxLength="500"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage(inputValue)}
          />
          <button className="kit19-send-btn" onClick={() => sendMessage(inputValue)}>
            <Send size={18} color="#fff" />
          </button>
        </div>
      </div>
    </>
  );
};

export default Kit19ChatWidget;
